import { useState } from "react"
import { useTranslation } from "react-i18next"
import { graphql, useFragment, useLazyLoadQuery } from "react-relay"
import { TableVirtuoso } from "react-virtuoso"
import { Archive } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { historyEventsTableFragment$key } from "@/modules/care-provider/components/edit-care-provider/history/__generated__/historyEventsTableFragment.graphql"
import { historyEventsTableLookupsQuery as historyEventsTableLookupsQueryType } from "@/modules/care-provider/components/edit-care-provider/history/__generated__/historyEventsTableLookupsQuery.graphql"
import { HistoryEventsRow } from "@/modules/care-provider/components/edit-care-provider/history/history-events-row"

export const historyEventsTableFragment = graphql`
  fragment historyEventsTableFragment on CareProviderHistoryEntry @relay(plural: true) {
    createdAt
    createdBy
    jsonPayload
  }
`

const historyEventsTableLookupsQuery = graphql`
  query historyEventsTableLookupsQuery($language: String!) {
    ch_specializationTranslations(where: { language: { eq: $language } }) {
      specializationId
      name
    }
    ch_specialtyTranslations(where: { language: { eq: $language } }) {
      specialtyId
      name
    }
    ch_officialLanguagesLookupTranslations(where: { language: { eq: $language } }) {
      officialLanguageLookupId
      isoLanguageName
    }
    displayableCountries(language: $language) {
      countryIsoCode2
      countryName
    }
  }
`

type HistoryEventsTableProps = {
  careProviderHistoryRef?: historyEventsTableFragment$key
}

export const HistoryEventsTable = ({ careProviderHistoryRef }: HistoryEventsTableProps) => {
  const { t, i18n } = useTranslation()
  const [isAtTop, setIsAtTop] = useState(true)
  const currentLanguage = i18n.language.toUpperCase()

  const histories = useFragment(historyEventsTableFragment, careProviderHistoryRef)

  const {
    ch_specializationTranslations,
    ch_specialtyTranslations,
    ch_officialLanguagesLookupTranslations,
    displayableCountries,
  } = useLazyLoadQuery<historyEventsTableLookupsQueryType>(historyEventsTableLookupsQuery, {
    language: currentLanguage,
  })

  return (
    <div className="h-full">
      <div className="bg-card h-195 overflow-hidden rounded-md border">
        <TableVirtuoso
          atTopStateChange={setIsAtTop}
          data={histories ?? []}
          components={{
            Table: (props) => (
              <table {...props} className="w-full table-fixed border-separate">
                {props.children}
              </table>
            ),
            TableHead: (props) => (
              <thead
                {...props}
                className={cn(
                  "px-4 py-2 whitespace-nowrap",
                  "after:bg-background after:absolute after:top-0 after:left-0 after:-z-10 after:h-full after:w-full",
                  !isAtTop && "shadow-xl",
                )}
              />
            ),
            TableRow: (props) => (
              <tr
                {...props}
                className="border-border bg-card group hover:bg-surface-hovered h-18 cursor-auto border-b text-2xl font-normal whitespace-nowrap transition-colors"
              />
            ),
            EmptyPlaceholder: (props) => {
              return (
                <tr {...props} className="bg-surface pointer-events-none h-[53vh] cursor-pointer text-sm">
                  <td colSpan={4} className="text-on-surface text-lg">
                    <div className="flex flex-col items-center justify-center gap-6">
                      <Button variant="circle" className="h-12 w-12 border">
                        <Archive />
                      </Button>
                      {t("patient.history.noHistory")}
                    </div>
                  </td>
                </tr>
              )
            },
          }}
          fixedHeaderContent={() => (
            <tr className="text-muted-foreground group border-border h-12 cursor-auto border-b text-left text-2xl font-medium transition-colors">
              <th className="text-on-surface-subdued w-28 rounded-none rounded-t-xl border-b px-4 py-2 text-sm font-medium">
                {t("patient.history.date")}
              </th>
              <th className="text-on-surface-subdued w-20 truncate rounded-none rounded-t-xl border-b px-4 py-2 text-sm font-medium">
                {t("patient.history.time")}
              </th>
              <th className="text-on-surface-subdued py w-65 truncate rounded-none rounded-t-xl border-b px-4 text-sm font-medium">
                {t("patient.history.updatedBy")}
              </th>
              <th className="text-on-surface-subdued truncate rounded-none rounded-t-xl border-b px-4 py-2 text-sm font-medium last-of-type:rounded-tr-md">
                {t("patient.history.logDetails")}
              </th>
            </tr>
          )}
          itemContent={(_, data) => {
            const { createdAt, createdBy, jsonPayload } = data
            return (
              <HistoryEventsRow
                createdAt={createdAt}
                createdBy={createdBy}
                jsonPayload={jsonPayload}
                isScrollable={!isAtTop}
                displayableCountries={displayableCountries}
                specializationTranslations={ch_specializationTranslations}
                specialtyTranslations={ch_specialtyTranslations}
                officialLanguagesTranslations={ch_officialLanguagesLookupTranslations}
              />
            )
          }}
        />
      </div>
    </div>
  )
}
